<script setup lang="ts">
import { useCityStore } from '@/store/city/city'
import { City, FormattedCity } from '@/store/city/city.types'

import LayoutConvert from 'convert-layout'
import CyrillicToTranslit from 'cyrillic-to-translit-js'
import { debounce } from 'throttle-debounce'

type Props = { searchQuery?: string | null }

const props = withDefaults(defineProps<Props>(), {
  searchQuery: null,
})

const { $metrics } = useNuxtApp()
const { isMobile } = useResponsive()

const cityStore = useCityStore()

const isCityListLoading = ref(false)

const cityList = computed<FormattedCity[]>(() => {
  // возвращаем полный список с добавлением страны и первой буквы города
  if (!props.searchQuery) return setCountryAndLetter(cityStore.cityList || [])
  // возвращаем отфильтрованный список с добавлением первой буквы города
  return setCountryAndLetter(
    filterListCities(cityStore.cityList || [], props.searchQuery),
  )
})

const isEmptyCityList = computed(() => cityList.value.length === 0)

watch(
  () => props.searchQuery,
  () => {
    nextTick(changeHeight)
    nextTick(changeHeight) // вызывается дважды, т.к. в safari и ios без этого есть проблемы
  },
)

onMounted(async () => {
  isCityListLoading.value = true

  await getCityList()

  isCityListLoading.value = false

  nextTick(changeHeight)

  window.addEventListener('resize', onResize, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

const getCityList = async () => {
  await cityStore.fetchCityList()
}

const cityListScrollbar = ref<HTMLElement | null>(null)
const cityListRef = ref<HTMLElement | null>(null)

// Фильтрация списка городов по строке
const filterListCities = (cityList: City[], searchQuery: string) => {
  searchQuery = searchQuery ? searchQuery.toLowerCase().trim() : ''

  return cityList.filter((city: City) => {
    const name: string = city.name.toLowerCase()

    return (
      name.includes(searchQuery) ||
      // в случае случайной установки раскладки на английский язык
      name.includes(LayoutConvert.ru.fromEn(searchQuery)) ||
      // в случае написания транслитом
      CyrillicToTranslit().transform(name).includes(searchQuery)
    )
  })
}

// Выделение совпадающих букв
const highlightMatchedLetters = (
  cityName: string,
  searchQuery?: string | null,
) => {
  // находим и выделяем совпадающие буквы
  if (!searchQuery) return cityName

  const name: string = cityName.toLowerCase()

  if (name.includes(LayoutConvert.ru.fromEn(searchQuery))) {
    searchQuery = LayoutConvert.ru.fromEn(searchQuery)
  } else {
    searchQuery = CyrillicToTranslit().reverse(searchQuery)
  }

  return cityName.replace(
    new RegExp(searchQuery, 'gi'),
    (match) =>
      `<span class="content-city-list__item-highlighted">${match}</span>`,
  )
}

// Определение страны с буквой и его городами
const setCountryAndLetter = (citiesList: City[]) => {
  const parameters: { [key: string]: Array<string> } = {}

  return citiesList.map((city: City) => {
    const firstCountry: string = city.country.name
    const firstLetter: string = city.visible_name[0]

    if (Object.prototype.hasOwnProperty.call(parameters, firstCountry)) {
      if (parameters[firstCountry].includes(firstLetter)) return city

      parameters[firstCountry].push(firstLetter)

      return { ...city, firstLetter }
    }

    parameters[firstCountry] = [firstLetter]

    return { ...city, firstLetter, firstCountry }
  })
}

const changeHeight = () => {
  // отслежваем и меняем высоту внутреннего блока со скролом
  if (!cityListScrollbar.value || !cityListRef.value) return

  cityListRef.value.style.height = 'auto'
  cityListRef.value.style.columnFill = 'balance'

  const heightCityListScrollbar = cityListScrollbar.value.clientHeight
  const heightList = cityListRef.value.clientHeight

  if (heightList < heightCityListScrollbar) {
    cityListRef.value.style.height = '100%'
    cityListRef.value.style.columnFill = 'auto'
  }

  cityListScrollbar.value.scrollTop = 0
}

// отложенный коллбэк создается в хуке
const onResize = debounce(200, changeHeight)

const cityClick = () => $metrics?.eventSendGoal({ ym: 'CITY_CHANGED' })
</script>

<template>
  <div v-if="isCityListLoading">
    <the-loader />
  </div>

  <div v-else-if="isEmptyCityList" class="content-empty">
    <div class="content-empty__wrapper">
      <the-illustration
        class="content-empty__image"
        name="not-found"
        alt="Ничего не найдено"
      />

      <span class="content-empty__text">
        Город не&nbsp;найден, уточните или&nbsp;введите другой
      </span>
    </div>
  </div>

  <div v-else ref="cityListScrollbar" class="content-city-list">
    <the-scroll-container class="content-city-list__scroll" show-bottom>
      <div ref="cityListRef" class="content-city-list__wrapper">
        <div
          v-for="city in cityList"
          :key="city.domain_prefix"
          class="content-city-list__item"
        >
          <div
            v-if="city?.firstCountry"
            class="content-city-list__item-country"
          >
            {{ city.firstCountry }}
          </div>

          <div class="content-city-list__item-city">
            <span
              v-if="city?.firstLetter && !isMobile"
              class="content-city-list__item-letter"
            >
              {{ city.firstLetter }}
            </span>

            <a
              class="content-city-list__item-link"
              :href="city.cityUrl"
              rel="nofollow"
              @click="cityClick"
              v-html="highlightMatchedLetters(city.visible_name, searchQuery)"
            />
          </div>
        </div>
      </div>
    </the-scroll-container>
  </div>
</template>

<style lang="scss" scoped>
.content-city-list {
  height: calc(100vh - 172px);

  @include scrollbar();

  &__scroll {
    height: calc(100vh - 172px);

    @include media('>=tablet') {
      display: inline-table;
      height: 100%;
    }
  }

  &__wrapper {
    height: auto;
    margin-right: 20px;
    column-fill: auto;
    columns: 1;

    @include media('>=tablet') {
      columns: 6;
      column-width: 160px;
      margin-right: 0;
    }
  }

  &__item {
    position: relative;
    padding-left: 12px;

    &:last-child {
      margin-bottom: 20px;
    }

    @include media('>=tablet') {
      padding-left: 20px;
    }

    &-city {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      padding: 8px 0;
      margin-top: 8px;

      @include media('>=tablet') {
        padding: 0;
        margin: 12px 0 0;
      }
    }

    &-country {
      position: relative;
      left: -12px;
      width: 136px;
      margin: 8px 0 20px;
      font-size: 16px;
      font-weight: 800;
      line-height: 20px;
      color: var(--ui-color-primary);

      @include media('>=tablet') {
        left: -20px;
        margin: 0 0 12px;
        font-size: 12px;
        font-weight: 600;
        line-height: 18px;
        color: var(--ui-color-sandy-700);
      }
    }

    &:not(:first-child) {
      .content-city-list__item-country {
        margin-top: 20px;

        @include media('>=tablet') {
          margin-top: 12px;
        }
      }
    }

    &-letter {
      position: absolute;
      left: 0;
      display: flex;
      align-items: center;
      height: 100%;
      font-size: 14px;
      font-weight: 800;
      line-height: 18px;
      color: var(--ui-color-primary);
    }

    &-link {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: var(--ui-text-primary);
      text-decoration: none;

      &:hover {
        color: var(--ui-color-primary);
      }

      @include media('>=tablet') {
        font-size: 14px;
        line-height: 18px;
      }
    }

    &__empty {
      height: 30px;
    }
  }
}

.content-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;

  @include media('>=tablet') {
    height: 100%;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__image {
    width: 120px;
    height: 120px;
    margin-bottom: 4px;

    @include media('>=tablet') {
      width: 180px;
      height: 180px;
      margin-bottom: 20px;
    }
  }

  &__text {
    width: 120px;
    font-size: 12px;
    font-weight: 800;
    line-height: 16px;
    color: var(--ui-text-primary);
    text-align: center;
    letter-spacing: -0.01em;

    @include media('>=tablet') {
      width: 270px;
      font-size: 20px;
      line-height: 26px;
    }
  }
}
</style>

<style lang="scss">
.content-city-list__item-highlighted {
  color: var(--ui-color-primary);
}
</style>
